import { TestAttributes } from '@va/types/component';
import { TooltipOptions } from '@va/types/tooltip';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useCheckOverflow, useMergeRefs } from '@va/util/hooks';
import classNames from 'classnames';
import { AriaAttributes, AriaRole, CSSProperties, PropsWithChildren, ReactNode, Ref, forwardRef, useRef } from 'react';
import { fontWeightClassName, fontWeights, paragraphSizeClassNames, paragraphSizes } from '.';
import { getTypographyClassNames } from './helpers';
import { TypographyProps, TypographyVariants } from './types';

export type ParagraphProps<T extends keyof TypographyVariants = 'interface'> = TestAttributes &
  TypographyProps<T> & {
    id?: string;
    weight?: fontWeights;
    colorClassName?: string;
    /**
     * @deprecated Use `variant` and `variantSize` instead.
     */
    size?: paragraphSizes;
    className?: string;
    children: ReactNode;
    style?: CSSProperties;
    onClick?: () => void;
    onMouseOver?: () => void;
    tabIndex?: number;
    role?: AriaRole;
    'aria-selected'?: AriaAttributes['aria-selected'];
  };

export const Paragraph = forwardRef(
  <T extends keyof TypographyVariants>(
    {
      id,
      weight = fontWeights.normal,
      colorClassName,
      size = paragraphSizes.normal,
      variant,
      variantSize,
      className,
      children,
      style,
      onClick,
      onMouseOver,
      ...rest
    }: PropsWithChildren<ParagraphProps<T>>,
    ref: Ref<HTMLParagraphElement>,
  ) => {
    const classes = classNames(
      'font-primary',
      fontWeightClassName(weight),
      { [paragraphSizeClassNames(size)]: !variant },
      getTypographyClassNames({ variant, variantSize }),
      colorClassName,
      className,
    );

    return (
      <p {...rest} id={id} ref={ref} style={style} className={classes} onClick={onClick} onMouseOver={onMouseOver}>
        {children}
      </p>
    );
  },
);

export const ParagraphWithTooltip = forwardRef(
  <T extends keyof TypographyVariants>(
    {
      id,
      weight = fontWeights.normal,
      colorClassName,
      size = paragraphSizes.normal,
      variant,
      variantSize,
      className,
      children,
      style,
      onClick,
      tooltipProps,
      'data-testid': dataTestId,
      'aria-selected': ariaSelected,
      role,
    }: ParagraphProps<T> & {
      tooltipProps?: TooltipOptions & { content?: ReactNode };
    },
    refProp: Ref<HTMLParagraphElement>,
  ) => {
    const ref = useRef<HTMLParagraphElement>(null);
    const mergedRef = useMergeRefs([ref, refProp]);
    const isOverflowing = useCheckOverflow(ref);

    const classes = classNames(
      'font-primary truncate',
      fontWeightClassName(weight),
      paragraphSizeClassNames(size),
      getTypographyClassNames({ variant, variantSize }),
      colorClassName,
      className,
    );

    return (
      <TooltipWrapper
        content={tooltipProps?.content ?? children}
        disabled={!isOverflowing}
        ref={mergedRef}
        {...tooltipProps}
      >
        <p
          id={id}
          style={style}
          className={classes}
          onClick={onClick}
          data-testid={dataTestId}
          role={role}
          aria-selected={ariaSelected}
        >
          {children}
        </p>
      </TooltipWrapper>
    );
  },
);
